// pages/master-data/vehicle/VehicleTypePage.jsx
'use client';
import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { useVehicleTypeList, deleteVehicleType } from 'stores/vehicleTypeStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
// import VehicleTypeAddEdit from './VehicleTypeAddEdit';
import { fleetApi } from '../../../api/axiosClient';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import { normalizeSearchFieldsConfig } from '../../../utils/searchHelper';
import { useLanguage } from '../../../utils/i18n';
import VehicleTypeAddEditV2 from './VehicleTypeAddEditV2';

function VehicleTypePageContent() {
  const { t } = useLanguage();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c), // <-- always string
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const companyFilterColumns = useMemo(() => {
    let storedCompanyId = null;
    try {
      if (typeof window !== 'undefined') storedCompanyId = localStorage.getItem('companyId');
    } catch {
      storedCompanyId = null;
    }

    if (selectedCompanies && selectedCompanies.length > 0) {
      const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
      if (hasAll) return [];
      const values = selectedCompanies.map((s) => String(s.value));
      if (values.length === 1) {
        return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] }];
      } else {
        return [{ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') }];
      }
    }

    if (storedCompanyId && String(storedCompanyId) !== 'all') {
      return [{ name: 'company_id', logic_operator: '=', operator: 'AND', value: String(storedCompanyId) }];
    }
    return [];
  }, [selectedCompanies]);

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [, setSearchExtraColumns] = useState([]);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'name', label: t('vehicleType.name', 'Vehicle Type Name') },
      { value: 'load_capacity', label: t('vehicleType.loadCapacity', 'Load Capacity') },
      { value: 'max_speed', label: t('vehicleType.maxSpeed', 'Max Speed') },
      { value: 'min_speed', label: t('vehicleType.minSpeed', 'Min Speed') },
      { value: 'average_speed', label: t('vehicleType.averageSpeed', 'Avg Speed') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  const joinDefs = useMemo(
    () => ({
      company: { name: 'm_company', column_results: ['name'] }
    }),
    []
  );

  const searchFieldsConfig = useMemo(() => normalizeSearchFieldsConfig(searchFieldOptions), [searchFieldOptions]);

  const filterCompanyIds = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';
    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';
    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  const hookSearchField = searchField === 'all' ? '' : searchField;
  const hookSearchValue = searchField === 'all' ? '' : searchRaw;

  const {
    data: vtFromHook,
    isLoading: hookLoading,
    totalRecord: hookTotal
  } = useVehicleTypeList(paginationModel.page, paginationModel.pageSize, hookSearchField, hookSearchValue, filterCompanyIds);

  const [localVT, setLocalVT] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localTotal, setLocalTotal] = useState(0);

  useEffect(() => {
    if (searchField !== 'all') return;
    const controller = new AbortController();
    const fetchAllSearch = async () => {
      setLocalLoading(true);
      try {
        // 1. Rakit Filter Perusahaan (columns)
        const columns = [];
        if (selectedCompanies && selectedCompanies.length > 0) {
          const values = selectedCompanies.map((s) => String(s.value));
          if (values.length === 1) {
            columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
          } else {
            columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
          }
        }

        if (columns.length === 0) {
          columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
        }

        // 2. Rakit Array 'filters' dinamis untuk pencarian global
        let filters = [];
        if (searchValue) {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'load_capacity', value: searchValue },
            { name: 'max_speed', value: searchValue },
            { name: 'min_speed', value: searchValue },
            { name: 'average_speed', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        }

        // 3. Susun Payload Utuh
        const payload = {
          paging: { start: paginationModel.page * paginationModel.pageSize, length: paginationModel.pageSize },
          orders: { columns: ['name'], ascending: true },
          joins: [
            { name: 'company', column_results: ['name'] },
            { name: 'axle_type', column_results: ['name'] }
          ],
          columns: columns,
          filters: filters
        };

        // Pastikan getTzParam() sudah di-import atau dibuat di helper jika tidak ada
        const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

        const resp = await fleetApi.post('/vehicle-type/search' + getTzParam(), payload, { signal: controller.signal });
        const data = resp?.data ?? [];
        setLocalVT(data);
        setLocalTotal(resp?.data?.totalRecord ?? data.length);
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        setLocalLoading(false);
      }
    };
    fetchAllSearch();
    return () => controller.abort();
  }, [searchField, searchValue, paginationModel, filterCompanyIds, searchFieldsConfig, joinDefs, companyFilterColumns, selectedCompanies]);

  const handleFetchAllForExport = async () => {
    try {
      const length = hookTotal > 0 ? hookTotal : 9999;
      const columns = [];
      if (selectedCompanies && selectedCompanies.length > 0) {
        const values = selectedCompanies.map((s) => String(s.value));
        if (values.length === 1) {
          columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: values[0] });
        } else {
          columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: values.join(',') });
        }
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      let filters = [];
      if (searchValue) {
        if (searchField === 'all') {
          filters = [
            { name: 'name', value: searchValue },
            { name: 'name', table_name: 'm_company', value: searchValue },
            { name: 'load_capacity', value: searchValue },
            { name: 'max_speed', value: searchValue },
            { name: 'min_speed', value: searchValue },
            { name: 'average_speed', value: searchValue },
            { name: 'description', value: searchValue }
          ];
        } else {
          filters = [{ name: searchField, logic_operator: 'like', operator: 'AND', value: searchValue }];
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [
          { name: 'company', column_results: ['name'] },
          { name: 'axle_type', column_results: ['name'] }
        ],
        columns: columns
      };

      if (searchField !== 'all' && searchValue) {
        payload.columns = [...payload.columns, ...filters];
      } else if (filters.length > 0) {
        payload.filters = filters;
      }

      const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;
      const resp = await fleetApi.post('/vehicle-type/search' + getTzParam(), payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('vehicle type export all error', err);
      return [];
    }
  };

  const rows = searchField === 'all' ? localVT : vtFromHook;
  const isLoading = searchField === 'all' ? localLoading : hookLoading;
  const totalRecords = searchField === 'all' ? localTotal : hookTotal;

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();

      if (!v) {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue('');
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      if (searchField === 'all') {
        setSearchExtraColumns([...companyFilterColumns]);
        setSearchValue(v);
        setPaginationModel((p) => ({ ...p, page: 0 }));
        return;
      }

      const searchCol = { name: searchField, logic_operator: 'like', operator: 'AND', value: v };
      setSearchExtraColumns([...companyFilterColumns, searchCol]);
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(t);
  }, [searchRaw, searchField, companyFilterColumns]);

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('eror');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteVehicleType(confirmTargetId);
      notify({ message: t('notify.deleted', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const getCompanyLabel = (row) => {
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id ?? null;
    if (companyId && companyOptions) {
      const found = companyOptions.find((c) => String(c.value) === String(companyId));
      if (found) return found.label;
    }
    return '-';
  };

  const columns = [
    {
      field: 'company',
      headerName: t('general.company', 'Company'),
      flex: 1,
      valueGetter: (value, row) => getCompanyLabel(row?.row ?? row),
      renderCell: (params) => getCompanyLabel(params.row)
    },
    { field: 'name', headerName: t('vehicleType.name', 'Name'), flex: 1 },
    { field: 'axle_type_name', headerName: 'Axle Layout', flex: 1, valueGetter: (value, row) => row?.join?.axle_type_name || '-' },
    {
      field: 'dimension',
      headerName: t('vehicleType.dimension', 'Dimension (LxWxH)(cm)'),
      flex: 2,
      excludeFromExport: true,
      renderCell: (params) => {
        const r = params.row || {};
        const toNum = (v) => {
          const n = Number(v);
          return Number.isFinite(n) ? Math.round(n) : 0;
        };

        const L = toNum(r.length_dimension ?? r.length ?? r.lengthDimension);
        const W = toNum(r.width_dimension ?? r.width ?? r.widthDimension);
        const H = toNum(r.height_dimension ?? r.height ?? r.heightDimension);

        return `${L} x ${W} x ${H}`;
      }
    },
    { field: 'length_dimension', headerName: t('vehicleType.length', 'Length'), hide: true },
    { field: 'width_dimension', headerName: t('vehicleType.width', 'Width'), hide: true },
    { field: 'height_dimension', headerName: t('vehicleType.height', 'Height'), hide: true },
    { field: 'load_capacity', headerName: t('vehicleType.loadCapacityKg', 'Load Capacity (kg)'), flex: 1 },
    { field: 'max_speed', headerName: t('vehicleType.maxSpeed', 'Max Speed'), flex: 1 },
    { field: 'min_speed', headerName: t('vehicleType.minSpeed', 'Min Speed'), flex: 1 },
    { field: 'average_speed', headerName: t('vehicleType.averageSpeed', 'Average Speed'), flex: 1 },
    { field: 'description', headerName: t('general.description', 'Description'), flex: 2 },

    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 100,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('vehicleType.master', 'Master Vehicle Type')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setSearchExtraColumns([...companyFilterColumns]);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.searchByName', 'Search by Name')}
        companyOptions={companyOptions}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      {/* FormDialog dipindahkan ke dalam VehicleTypeAddEdit */}
      {/* <VehicleTypeAddEdit
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p })); // Refresh
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.failedSave', 'Operation failed'), severity: 'error' });
        }}
      /> */}
      <VehicleTypeAddEditV2
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        onSuccess={(msg) => {
          setPaginationModel((p) => ({ ...p })); // Refresh
        }}
        onError={(msg) => {
          notify({ message: msg || t('notify.failedSave', 'Operation failed'), severity: 'error' });
        }}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function VehicleTypePage() {
  return (
    <>
      <VehicleTypePageContent />
    </>
  );
}
